/**
 * Shops, cafés, takeaways and services that join as partners (ruling 65, 16 and 17 October
 * 2026). The pure rules live here, with every figure coming from `extras.partners` in
 * `config/store.json`: what a partner pays, how many free listings there are, and which kinds of
 * service are never listed. A café, restaurant or takeaway owes no commission (Anthony, 30
 * October 2026): it may agree a discount for Ozi instead (restaurants.ts).
 */
/** What kind of business a partner is. A service lists services, not goods. */
export const PARTNER_KINDS = ['shop', 'food', 'service'];
export const PARTNER_KIND_WORDS = {
    shop: 'Shop',
    food: 'Café or takeaway',
    service: 'Service',
};
/**
 * The three packages a shop can be on (ruling 96, 5 November 2026): Free, Shop Partner Plus and
 * Shop Partner Pro. A café, takeaway or service is on the free one unless it is a Preferred
 * Partner (Plus free) or has bought Pro.
 */
export const PARTNER_PLANS = ['free', 'plus', 'pro'];
/** Whether a plan is one that is paid for. */
export function paidPlan(plan) {
    return plan === 'plus' || plan === 'pro';
}
/** What a package costs a month: nothing for Free. */
export function planMonthlyPence(plan, pricing) {
    if (plan === 'plus')
        return pricing.plusMonthlyPence;
    if (plan === 'pro')
        return pricing.proMonthlyPence;
    return 0;
}
/**
 * The package whose limits apply now: the one chosen while it is paid, Free when a paid one has
 * lapsed (ruling 96: products beyond Free's limit and photos beyond its one are hidden, never
 * deleted).
 */
export function effectivePlan(shop) {
    if (paidPlan(shop.plan))
        return shop.plusPaid ? shop.plan : 'free';
    return 'free';
}
/**
 * What a shop may do now (ruling 96): the limits of the package that applies. A café, takeaway
 * or service has no product count, as it never had, and Plus's other limits while on Free, so
 * its menu keeps its photos; a Preferred Partner's Plus is paid for it; Pro bought is Pro.
 */
export function planLimits(shop, pricing) {
    const effective = effectivePlan(shop);
    const base = shop.kind !== 'shop' && effective === 'free' ? pricing.plans.plus : pricing.plans[effective];
    return { ...base, products: shop.kind === 'shop' ? pricing.plans[effective].products : null };
}
/** "10%" from 1000 basis points; "12.5%" from 1250. */
export function basisPointsWords(basisPoints) {
    const percent = basisPoints / 100;
    return `${Number.isInteger(percent) ? percent : percent.toFixed(1)}%`;
}
/** Lower case, letters and digits only, single spaces: how words are compared here. */
function plain(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
/**
 * The excluded kind of service found in what a business says about itself, or null. Each
 * excluded kind is found as whole words ("loans" is found in "payday loans", never in "sloane
 * street"), so a hairdresser is never refused for a word inside another.
 */
export function excludedServiceKind(text, excluded) {
    const said = ` ${plain(text)} `;
    // The most specific kind named ("payday loans" before "loans"), so the refusal says exactly what.
    const found = excluded
        .filter((kind) => plain(kind) !== '' && said.includes(` ${plain(kind)} `))
        .sort((a, b) => plain(b).length - plain(a).length);
    return found[0] ?? null;
}
/** The plain words a refused business is told. */
export function excludedServiceWords(kind, productName) {
    return `${productName} cannot list ${kind}, or anything that needs a licence from the FCA, the SRA or the MHRA. We are sorry, and we wish you well.`;
}
/** Whether a new shop joining now gets one of the free-for-ever listings. */
export function freeListingAvailable(freeListedSoFar, pricing) {
    return freeListedSoFar < pricing.freeListingCount;
}
/**
 * How many products a partner may have listed (live or waiting), or null for no limit: Free 10,
 * Plus 300, Pro 3000 (ruling 96), from `extras.partners.plans`; a café, takeaway or service has
 * no limit.
 */
export function productLimit(shop, pricing) {
    return planLimits(shop, pricing).products;
}
/* ------------------------------------------------------------------ units and unit prices */
/** The units a product may be sold in (ruling 96). */
export const UNIT_KINDS = ['g', 'kg', 'ml', 'l', 'each', 'pack'];
export const UNIT_WORDS = {
    g: 'grams',
    kg: 'kilograms',
    ml: 'millilitres',
    l: 'litres',
    each: 'each',
    pack: 'in a pack',
};
/** "500 g", "1.5 kg", "6 each", "4 in a pack": the quantity as the label would print it. */
export function unitQuantityWords(quantity, unit) {
    const amount = Number.isInteger(quantity)
        ? String(quantity)
        : String(Math.round(quantity * 100) / 100);
    if (unit === 'each')
        return quantity === 1 ? '1' : `${amount} each`;
    if (unit === 'pack')
        return `${amount} in a pack`;
    return `${amount} ${unit}`;
}
/**
 * The unit price the Price Marking Order 2004 asks shops to show beside a grocery's price: per
 * kilogram for anything by weight, per litre for anything by volume, each for things counted,
 * and per item for a pack. Null when the quantity makes no sense.
 */
export function unitPrice(pricePence, quantity, unit) {
    if (!(quantity > 0) || !Number.isFinite(quantity) || pricePence <= 0)
        return null;
    switch (unit) {
        case 'g':
            return { pence: Math.round((pricePence * 1000) / quantity), per: 'kg' };
        case 'kg':
            return { pence: Math.round(pricePence / quantity), per: 'kg' };
        case 'ml':
            return { pence: Math.round((pricePence * 1000) / quantity), per: 'litre' };
        case 'l':
            return { pence: Math.round(pricePence / quantity), per: 'litre' };
        case 'each':
            return { pence: Math.round(pricePence / quantity), per: 'each' };
        case 'pack':
            return { pence: Math.round(pricePence / quantity), per: 'item' };
    }
}
/** "£2.40 per kg", "80p each", from a unit price and a money formatter. */
export function unitPriceWords(price, money) {
    return price.per === 'each'
        ? `${money(price.pence)} each`
        : `${money(price.pence)} per ${price.per}`;
}
/** The first part of a UK postcode ("ME7 1ER" gives "ME7"), the area a shop is in. */
export function postcodeArea(postcode) {
    const tidy = postcode.toUpperCase().replace(/\s+/g, '');
    const match = /^([A-Z]{1,2}\d[A-Z\d]?)\d[A-Z]{2}$/.exec(tidy);
    if (match)
        return match[1];
    return tidy.replace(/\d[A-Z]{2}$/, '') || tidy;
}
//# sourceMappingURL=partners.js.map